import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Row, Col, Badge, Button, ButtonGroup, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentViewer from './viewers/DocumentViewer';
import AttachmentViewer from './common/AttachmentViewer';
import type { BankTransaction, Invoice, Payment } from '../api/generated/model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  postBankTransactionsTransactionIdPayment,
  postBankTransactionPayments,
  postInvoicePaymentsPaymentIdSync,
  postBankTransactionsTransactionIdSync,
  patchBankTransactionsTransactionId,
  getBankTransactionsTransactionId,
  getBankTransactionsTransactionIdPaymentCandidates,
  getBankTransactionsTransactionIdAttachmentsAttachmentId,
  deleteBankTransactionPaymentsId,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import { methodLabel, paymentFormatLabel, formatDateTime, kindLabel } from '../utils/bankTransaction';
import { CopyButton } from './common/Copyable';
import RemittancePanel from './RemittancePanel';
import InvoicePicker from './common/InvoicePicker';
import InvoiceDetailModal from './InvoiceDetailModal';
import PaymentDetailModal from './PaymentDetailModal';
import PaymentsTable from './PaymentsTable';

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

interface TransactionDetailModalProps {
  transaction: BankTransaction;
  onClose: () => void;
  onUpdated: () => void;
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({ transaction: initialTxn, onClose, onUpdated }) => {
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  // Local mirror of the transaction. The parent passes the initial value
  // as a prop; after any mutation in this modal (link, unlink, skip,
  // create-payment, etc.) we re-fetch from the API so the modal reflects
  // the new state without needing the parent to re-pass the prop.
  const [transaction, setTransaction] = useState<BankTransaction>(initialTxn);
  // Re-sync if the parent swaps the prop (different transaction opened).
  useEffect(() => { setTransaction(initialTxn); }, [initialTxn]);

  const refetchTransaction = useCallback(async () => {
    if (!initialTxn.id) return;
    try {
      const fresh = await getBankTransactionsTransactionId(initialTxn.id);
      setTransaction(fresh as BankTransaction);
    } catch {
      // Best-effort; the parent's onUpdated will repopulate eventually.
    }
  }, [initialTxn.id]);

  // Compose: refresh local + bubble to parent so list views invalidate too.
  const refreshAndNotify = useCallback(async () => {
    await refetchTransaction();
    onUpdated();
  }, [refetchTransaction, onUpdated]);

  const isIncoming = (transaction.amount || 0) > 0;
  const isCheck = transaction.kind === 'check_deposit';
  const attachments = transaction.attachments || [];
  const hasAttachments = attachments.length > 0;
  // Link state derived from BTPs returned alongside the transaction.
  // 1:N is supported (one deposit can fund multiple Payments in QB).
  const linkedPayments = transaction.linked_payments || [];
  const isLinked = linkedPayments.length > 0;
  // Category 'other' = admin marked "not an invoice payment" — explicit
  // decision, reversible via Undo.
  const isMarkedOther = transaction.category === 'other';
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null);

  // Check image state (for check deposits with front/back toggle)
  const checkFrontAttachment = attachments.find(a => a.file_name === 'Check Front');
  const checkBackAttachment = attachments.find(a => a.file_name === 'Check Back');
  const hasCheckImages = isCheck && (checkFrontAttachment || checkBackAttachment);

  const [imageSide, setImageSide] = useState<'front' | 'back'>('front');
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const currentCheckAttachment = imageSide === 'front' ? checkFrontAttachment : checkBackAttachment;

  useEffect(() => {
    if (!hasCheckImages || !currentCheckAttachment?.id || !transaction.id) return;

    let cancelled = false;
    setImageLoading(true);
    setImageBlobUrl(null);

    (async () => {
      try {
        const blob = await getBankTransactionsTransactionIdAttachmentsAttachmentId(transaction.id!, currentCheckAttachment.id!) as unknown as Blob;
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setImageBlobUrl(url);
      } catch {
        if (!cancelled) setImageBlobUrl(null);
      } finally {
        if (!cancelled) setImageLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [hasCheckImages, currentCheckAttachment?.id, transaction.id]);

  useEffect(() => {
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, []);

  // Invoice picker state. The picker handles its own debounced search;
  // we just hold the chosen value and the optional initial search seed
  // that we pre-fill from the bank memo.
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceSearchSeed, setInvoiceSearchSeed] = useState<string | undefined>(undefined);
  const [invoicePickerKey, setInvoicePickerKey] = useState(0);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);

  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState(String(Math.abs(transaction.amount || 0)));
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState(() => {
    if (transaction.date) {
      return new Date(transaction.date).toISOString().split('T')[0];
    }
    return '';
  });
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Linker mode for unlinked transactions. Default is "link" — most bank
  // deposits correspond to a Payment QB already created, so the common
  // case is matching to an existing Payment, not creating a new one.
  // "categorize" is the explicit "this isn't an invoice payment" path
  // (future: refund / fee / transfer / etc.).
  type LinkMode = 'link' | 'create' | 'categorize';
  const [mode, setMode] = useState<LinkMode>('link');
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);

  // Candidate Payments — what the auto-linker would suggest. Drives the
  // PaymentsTable's "Show: Candidates" pre-checked filter.
  const { data: candidatesData } = useQuery({
    queryKey: ['bt-payment-candidates', transaction.id],
    queryFn: () => getBankTransactionsTransactionIdPaymentCandidates(transaction.id!),
    enabled: !!transaction.id && isIncoming && !isLinked && !isMarkedOther && mode === 'link',
  });
  const candidatePayments: Payment[] = useMemo(
    () => ((candidatesData as any)?.data || []) as Payment[],
    [candidatesData]
  );

  // Per-row "Revalidate" handler for the candidate Payments table — POSTs
  // /invoice-payments/{paymentId}/sync (paymentId is composite
  // "<src_id>:<id>", which the API already serves on Payment.id). The
  // backend refetches from QB and runs the auto-linker; on success the
  // candidate may have been linked. refreshAndNotify() refetches the
  // local transaction so transaction.payments now contains the new BTP
  // — that flips isLinked, which hides the candidate picker entirely
  // and shows the linked-payment view (matching the post-hard-reload
  // state). Without this the picker stays mounted with stale empty
  // candidatePayments and falls back to "all payments" via the API.
  const handleRevalidatePayment = useCallback(async (p: Payment) => {
    const id = p.id || '';
    if (!id) return;
    try {
      await postInvoicePaymentsPaymentIdSync(id);
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['bt-payment-candidates', transaction.id] });
      await refreshAndNotify();
    }
  }, [queryClient, transaction.id, refreshAndNotify]);

  // "Resync" handler for the BT itself — POSTs /bank-transactions/{id}/sync.
  // Pulls the latest from upstream (Mercury for live accounts) and runs
  // the auto-linker. refreshAndNotify reloads the local txn so any new
  // link / cleared blocker is visible without a hard reload.
  const [resyncing, setResyncing] = useState(false);
  const handleResync = useCallback(async () => {
    if (!transaction.id) return;
    setResyncing(true);
    try {
      await postBankTransactionsTransactionIdSync(transaction.id);
      await queryClient.invalidateQueries({ queryKey: ['bt-payment-candidates', transaction.id] });
      await refreshAndNotify();
    } catch (e: any) {
      setError(e?.response?.data?.message || `Failed to resync: ${e}`);
    } finally {
      setResyncing(false);
    }
  }, [transaction.id, queryClient, refreshAndNotify]);


  // Auto-match seed: pre-fill the picker's search box with whatever
  // invoice-looking number we can find in the bank memo.
  useEffect(() => {
    const memoText = transaction.external_memo || transaction.note || '';
    if (memoText && !selectedInvoice) {
      const match = memoText.match(/#?(\d{3,})/);
      if (match) {
        setInvoiceSearchSeed(match[1]);
        setInvoicePickerKey(k => k + 1); // force picker to pick up the new seed
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction]);

  const handleCreatePayment = async () => {
    if (!selectedInvoice) return;
    setSubmitting(true);
    setError(null);
    try {
      await postBankTransactionsTransactionIdPayment(transaction.id!, {
        invoices: [{
          invoice_src_id: selectedInvoice.src_id!,
          invoice_id: selectedInvoice.id!,
          amount: parseFloat(paymentAmount),
        }],
        ...(checkNumber ? { check_number: checkNumber } : {}),
        ...(checkDate ? { check_date: checkDate } : {}),
        ...(memo ? { memo } : {}),
        matched_by: 'manual',
      });
      await refreshAndNotify();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveLink = async () => {
    if (selectedPaymentIds.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      // selectedPaymentIds are composite "<src_id>:<id>" strings. POST one
      // BTP per checked Payment.
      for (const composite of selectedPaymentIds) {
        const idx = composite.indexOf(':');
        if (idx <= 0) continue;
        const srcId = composite.substring(0, idx);
        const pid = composite.substring(idx + 1);
        await postBankTransactionPayments({
          transaction_id: transaction.id!,
          payment_src_id: srcId,
          payment_id: pid,
        });
      }
      // Reset selection so the post-link state reads cleanly when the
      // modal stays open and the user keeps reviewing the transaction.
      setSelectedPaymentIds([]);
      await refreshAndNotify();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to link payment(s)');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkNotInvoicePayment = async () => {
    try {
      await patchBankTransactionsTransactionId(transaction.id!, [
        { op: 'replace', path: '/category', value: 'other' },
      ]);
      await refreshAndNotify();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update transaction');
    }
  };

  const checkImageDocs = useMemo(() =>
    imageBlobUrl ? [{ uri: imageBlobUrl }] : [],
    [imageBlobUrl]
  );

  const checkImageStyle = useMemo(() => ({ flex: 1 as const, minHeight: 0 }), []);

  const checkImageToolbar = useMemo(() => (
    <>
      <Button size="sm" variant={imageSide === 'front' ? 'primary' : 'outline-secondary'} onClick={() => setImageSide('front')}>Front</Button>
      <Button size="sm" variant={imageSide === 'back' ? 'primary' : 'outline-secondary'} onClick={() => setImageSide('back')}>Back</Button>
    </>
  ), [imageSide]);

  const handleUndoNotInvoicePayment = async () => {
    try {
      await patchBankTransactionsTransactionId(transaction.id!, [
        { op: 'replace', path: '/category', value: null },
      ]);
      await refreshAndNotify();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update transaction');
    }
  };

  // Non-check attachment download function
  const attachmentDownloadFn = useCallback(async (attachmentId: string): Promise<Blob> => {
    const rawBlob = await getBankTransactionsTransactionIdAttachmentsAttachmentId(transaction.id!, attachmentId);
    return rawBlob as unknown as Blob;
  }, [transaction.id]);

  return (
    <>
    <Modal show={!openPaymentId && !invoiceModalId} onHide={onClose} size="xl" scrollable dialogClassName="transaction-detail-modal modal-95w" fullscreen="lg-down">
      {/* Force modal-content to a definite height so the percentage-
          height chain inside (DataTable's `maxHeight=100%`) resolves
          and the table can scroll internally. Bootstrap's default
          `max-height` leaves modal-content's height as `auto`, which
          breaks the chain. Trade-off: modal is always near-full
          viewport regardless of content size. */}
      <style>{`.transaction-detail-modal .modal-content { height: calc(100vh - 3.5rem); }`}</style>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2 w-100">
          <span className="d-flex align-items-center gap-2">
            <span>{formatDateTime(transaction.date)}</span>
            <span className="text-muted">|</span>
            <span>{isIncoming ? 'Deposit' : 'Payment'}</span>
            <span className="text-muted">|</span>
            <span style={{ color: isIncoming ? '#198754' : '#dc3545' }}>
              {formatCurrency(transaction.amount)}
            </span>
          </span>
          <div className="d-flex align-items-center gap-2 ms-auto me-3">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={handleResync}
              disabled={resyncing || !transaction.id}
              title="Refetch this bank transaction from its source and re-run the auto-linker"
              className="d-inline-flex align-items-center gap-1"
            >
              {resyncing
                ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span>Resyncing…</span></>
                : <><i className="bi bi-arrow-clockwise" /><span>Resync</span></>}
            </Button>
            {transaction.dashboard_link && (
              <a
                href={transaction.dashboard_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Mercury
              </a>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 d-flex flex-column gap-3" style={{ minHeight: 0 }}>
        {/* Transaction Details */}
        <Row className="mb-3" style={{ flexShrink: 0 }}>
          <Col md={6}>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr><td className="text-muted" style={{ width: '120px' }}>Date</td><td>{formatDateTime(transaction.date)}</td></tr>
                {transaction.posted_at && <tr><td className="text-muted">Posted</td><td>{formatDateTime(transaction.posted_at)}</td></tr>}
                <tr><td className="text-muted">Status</td><td><Badge bg={transaction.status === 'sent' ? 'success' : 'secondary'}>{transaction.status}</Badge></td></tr>
              </tbody>
            </table>
          </Col>
          <Col md={6}>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr><td className="text-muted" style={{ width: '120px' }}>Type</td><td>{kindLabel(transaction.kind)}</td></tr>
                <tr><td className="text-muted">Account</td><td>{transaction.account_name}</td></tr>
                <tr><td className="text-muted">Counterparty</td><td>{transaction.counterparty_name}</td></tr>
                {transaction.method && <tr><td className="text-muted">Method</td><td>{methodLabel(transaction.method)}</td></tr>}
                {transaction.payment_format && <tr><td className="text-muted">Format</td><td>{paymentFormatLabel(transaction.payment_format)}</td></tr>}
                {transaction.trace_number && (
                  <tr>
                    <td className="text-muted">Trace #</td>
                    <td>
                      <code className="me-2">{transaction.trace_number}</code>
                      <CopyButton
                        value={transaction.trace_number || ''}
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                      />
                    </td>
                  </tr>
                )}
                {transaction.sender_trace && (
                  <tr>
                    <td className="text-muted">Sender Trace</td>
                    <td style={{ wordBreak: 'break-all', paddingRight: '1rem' }}>
                      <code className="me-2">{transaction.sender_trace}</code>
                      <CopyButton
                        value={transaction.sender_trace || ''}
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                      />
                    </td>
                  </tr>
                )}
                {transaction.note && <tr><td className="text-muted">Note</td><td style={{ wordBreak: 'break-word', paddingRight: '1rem' }}>{transaction.note}</td></tr>}
                {transaction.external_memo && <tr><td className="text-muted">Memo</td><td style={{ wordBreak: 'break-all', paddingRight: '1rem' }}>{transaction.external_memo}</td></tr>}
              </tbody>
            </table>
          </Col>
        </Row>

        {/* Remittance — bank-decoded + admin-editable manual entries.
            Source of truth the auto-linker uses. Incoming-only: outgoing
            payments have no consumer for remittance edits (the linker
            only operates on inbound deposits). */}
        {transaction.id && isIncoming && (
          <div style={{ flexShrink: 0 }}>
            <hr />
            <RemittancePanel transactionId={transaction.id} />
          </div>
        )}

        {/* Invoice Payment Mapping (incoming only).
            `flex: 1 1 0` so this section and the check-image viewer
            below share modal-body's free space when there's room.
            `min-height: 280px` is the floor — flex won't shrink it
            below this. When the floor + the check viewer's floor
            exceeds the body, the body's scrollbar engages (modal is
            `scrollable`). The floor MUST live on this outer wrapper,
            not on inner content — inner `min-height` doesn't stop
            outer flex shrink, it just causes content to overflow
            downward into the next sibling. */}
        {isIncoming && (
          <div className="d-flex flex-column" style={{ flex: '1 1 0', minHeight: '280px' }}>
            <hr />
            <h6 className="text-muted mb-3">Invoice Payment</h6>

            {isLinked && (
              <div>
                <Alert variant="success" className="mb-2">
                  <i className="bi bi-check-circle me-2"></i>
                  Linked to {linkedPayments.length === 1 ? 'payment' : `${linkedPayments.length} payments`}.
                  Click a row to open the payment (void / delete / mark unlinkable from there).
                </Alert>
                <table className="table table-sm mb-3">
                  <thead>
                    <tr className="text-muted">
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Customer</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Invoices</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedPayments.map(lp => {
                      const apps = (lp as any).applied_invoices || [];
                      return (
                      <tr
                        key={lp.btp_id || lp.payment_id}
                        style={{ cursor: lp.payment_id ? 'pointer' : 'default' }}
                        onClick={() => lp.payment_id && setOpenPaymentId(lp.payment_id)}
                      >
                        <td>{lp.txn_date ? new Date(lp.txn_date as any).toLocaleDateString() : '—'}</td>
                        <td>{lp.total_amount ? formatCurrency(parseFloat(lp.total_amount)) : '—'}</td>
                        <td>{lp.customer_name || <span className="text-muted">—</span>}</td>
                        <td>{lp.method ? lp.method.toUpperCase() : <span className="text-muted">—</span>}</td>
                        <td>{lp.reference || <span className="text-muted">—</span>}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {apps.length === 0 ? (
                            <span className="text-muted">—</span>
                          ) : apps.map((a: any, i: number) => {
                            const compositeId = a.invoice_src_id && a.invoice_id
                              ? `${a.invoice_src_id}-${a.invoice_id}`
                              : null;
                            const label = a.invoice_number || a.invoice_id || '';
                            return (
                              <React.Fragment key={`${a.invoice_src_id}:${a.invoice_id}:${i}`}>
                                {i > 0 && <span className="text-muted">, </span>}
                                <Button
                                  variant="link"
                                  className="p-0 text-decoration-none align-baseline"
                                  onClick={() => compositeId && setInvoiceModalId(compositeId)}
                                  disabled={!compositeId}
                                  title={a.amount ? `${label} — ${formatCurrency(parseFloat(a.amount))}` : label}
                                >
                                  {label}
                                </Button>
                              </React.Fragment>
                            );
                          })}
                        </td>
                        <td>
                          <Badge bg={lp.matched_by === 'auto' ? 'info' : 'success'}>
                            {lp.matched_by === 'auto' ? 'auto-linked' : 'manually linked'}
                          </Badge>
                        </td>
                        <td className="text-end" onClick={(e) => e.stopPropagation()}>
                          {lp.matched_by === 'manual' && lp.btp_id && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm('Unlink this manual link? Neither the payment nor the bank transaction is touched — only the link between them is removed.')) return;
                                try {
                                  await deleteBankTransactionPaymentsId(lp.btp_id!);
                                  await refreshAndNotify();
                                } catch (e: any) {
                                  setError(e?.response?.data?.message || `Failed to unlink: ${e}`);
                                }
                              }}
                              title="Unlink (removes the link without touching the payment or bank transaction)"
                            >
                              Unlink
                            </Button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {isMarkedOther && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <Alert variant="secondary" className="mb-0 flex-grow-1">
                  <i className="bi bi-tag me-2"></i>
                  Marked as not an invoice payment
                </Alert>
                <Button variant="outline-primary" size="sm" onClick={handleUndoNotInvoicePayment}>Undo</Button>
              </div>
            )}

            {!isLinked && !isMarkedOther && (
              <>
                <div className="mb-2 flex-shrink-0">
                  <ButtonGroup size="sm">
                    <Button
                      variant={mode === 'link' ? 'primary' : 'outline-primary'}
                      onClick={() => setMode('link')}
                    >
                      <i className="bi bi-link-45deg me-1"></i>
                      Link Existing Payment
                    </Button>
                    <Button
                      variant={mode === 'create' ? 'primary' : 'outline-primary'}
                      onClick={() => setMode('create')}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Create Payment
                    </Button>
                    <Button
                      variant={mode === 'categorize' ? 'primary' : 'outline-primary'}
                      onClick={() => setMode('categorize')}
                    >
                      <i className="bi bi-tag me-1"></i>
                      Categorize
                    </Button>
                  </ButtonGroup>
                </div>

                {mode === 'link' && (
                  <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                    <PaymentsTable
                      candidatePayments={candidatePayments}
                      selectedIds={selectedPaymentIds}
                      onSelectionChange={setSelectedPaymentIds}
                      onRowClick={(p) => p.id && setOpenPaymentId(p.id)}
                      onRevalidate={handleRevalidatePayment}
                      preferencesKey="tdm-link-payments"
                      title="Payments"
                      showDateNav={true}
                      userId={user?.id}
                      userPreferences={userData?.preferences}
                    />
                  </div>
                )}

                {mode === 'categorize' && (
                  <div className="d-flex flex-column gap-2 mb-2">
                    <div className="text-muted small mb-1">
                      Mark this transaction as something other than an
                      invoice payment so the auto-linker leaves it alone.
                    </div>
                    <div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleMarkNotInvoicePayment}
                        disabled={submitting}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Not an invoice payment
                      </Button>
                    </div>
                  </div>
                )}

                {mode === 'create' && (
                  <div className="d-flex gap-2 align-items-end flex-wrap mb-2">
                    <Form.Group style={{ minWidth: '260px', flex: '1 1 320px' }}>
                      <Form.Label className="mb-0 small">Invoice</Form.Label>
                      <InvoicePicker
                        key={invoicePickerKey}
                        value={selectedInvoice}
                        onChange={setSelectedInvoice}
                        onOpenInvoiceDetail={setInvoiceModalId}
                        initialSearch={invoiceSearchSeed}
                        size="sm"
                      />
                    </Form.Group>
                    <Form.Group style={{ width: '100px' }}>
                      <Form.Label className="mb-0 small">Amount</Form.Label>
                      <Form.Control type="number" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} size="sm" />
                    </Form.Group>
                    {isCheck && (
                      <>
                        <Form.Group style={{ width: '90px' }}>
                          <Form.Label className="mb-0 small">Check #</Form.Label>
                          <Form.Control type="text" value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} size="sm" />
                        </Form.Group>
                        <Form.Group style={{ width: '140px' }}>
                          <Form.Label className="mb-0 small">Check Date</Form.Label>
                          <Form.Control type="date" value={checkDate} onChange={(e) => setCheckDate(e.target.value)} size="sm" />
                        </Form.Group>
                      </>
                    )}
                    <Form.Group style={{ flex: 1, minWidth: '120px' }}>
                      <Form.Label className="mb-0 small">Memo</Form.Label>
                      <Form.Control type="text" placeholder="optional" value={memo} onChange={(e) => setMemo(e.target.value)} size="sm" />
                    </Form.Group>
                  </div>
                )}

                {error && <Alert variant="danger" className="py-1 mb-0 flex-shrink-0">{error}</Alert>}
              </>
            )}
          </div>
        )}

        {/* Check Images (inline viewer with front/back toggle) */}
        {hasCheckImages && (
          <>
            <hr style={{ flexShrink: 0 }} />
            {/* flex: 1 1 0 — shares equally with the Invoice Payment
                section above. `min-height: 240px` is the floor; flex
                won't shrink below it. Once both floors exceed the
                modal-body, the body's scrollbar engages. */}
            <div style={{ flex: '1 1 0', minHeight: '240px', background: '#e9ecef', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}>
              {imageLoading && <div className="text-center py-5"><Spinner size="sm" /> Loading...</div>}
              {imageBlobUrl && (
                <DocumentViewer
                  documents={checkImageDocs}
                  style={checkImageStyle}
                  initialViewMode="single-page"
                  toolbarExtra={checkImageToolbar}
                />
              )}
              {!imageLoading && !imageBlobUrl && (
                <div className="text-muted text-center py-5">Image not available</div>
              )}
            </div>
          </>
        )}

        {/* Non-check attachments (modal-based viewer) */}
        {!isCheck && hasAttachments && (
          <div style={{ flexShrink: 0 }}>
            <hr />
            <AttachmentViewer
              attachments={attachments}
              downloadFn={attachmentDownloadFn}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>Close</Button>
          {isIncoming && !isLinked && !isMarkedOther && mode === 'link' && (
            <Button
              variant="primary"
              onClick={handleSaveLink}
              disabled={selectedPaymentIds.length === 0 || submitting}
            >
              {submitting && <Spinner animation="border" size="sm" className="me-1" />}
              Save Link{selectedPaymentIds.length > 1 ? ` (${selectedPaymentIds.length})` : ''}
            </Button>
          )}
          {isIncoming && !isLinked && !isMarkedOther && mode === 'create' && (
            <Button
              variant="primary"
              onClick={handleCreatePayment}
              disabled={!selectedInvoice || submitting || !paymentAmount}
            >
              {submitting && <Spinner animation="border" size="sm" className="me-1" />}
              Create Payment
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
    {invoiceModalId && (
      <InvoiceDetailModal
        show
        invoiceId={invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
      />
    )}
    {openPaymentId && (
      <PaymentDetailModal
        paymentId={openPaymentId}
        onClose={() => setOpenPaymentId(null)}
        onChanged={refreshAndNotify}
      />
    )}
    </>
  );
};

export default TransactionDetailModal;
